import {
  type InputEntryFunctionData,
  Network,
  TypeTagAddress,
  TypeTagU64,
} from "@aptos-labs/ts-sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { amountToChainUnits } from "@/utils/math";

import { getSdkConfig, readDexSdk } from "../dex-sdk";
import { accountRestrictedMintAvailableQueryKey } from "../queries/account-restricted-mint-available-testnet";
import { dailyRestrictedMintsRemainingQueryKey } from "../queries/daily-restricted-mints-remaining-testnet";
import { triggerResetMintTsQueryKey } from "../queries/trigger-reset-mint-ts-testnet";
import { usdcBalanceQueryKey } from "../queries/usdc-balance";
import { useWalletFeePayerWithFallback } from "./wallet-fee-payer";

export function useMintUsdc() {
  const queryClient = useQueryClient();
  const { accountAddress } = useDecibelWallet();
  const { mutateAsync: signAndSubmitWalletTransaction } = useWalletFeePayerWithFallback();
  const config = getSdkConfig();
  const isTestnet = config.network === Network.TESTNET;

  return useMutation({
    mutationFn: async (amount: number) => {
      if (!accountAddress) {
        throw new Error("Wallet not connected");
      }

      const data: InputEntryFunctionData = {
        function: `${readDexSdk.config.deployment.package}::usdc::${isTestnet ? "restricted_mint" : "mint"}`,
        typeArguments: [],
        functionArguments: isTestnet
          ? [amountToChainUnits(amount)]
          : [accountAddress, amountToChainUnits(amount)],
        abi: isTestnet
          ? {
              typeParameters: [],
              parameters: [new TypeTagU64()],
            }
          : {
              typeParameters: [],
              parameters: [new TypeTagAddress(), new TypeTagU64()],
            },
      };

      const pendingTransaction = await signAndSubmitWalletTransaction({ data });
      await readDexSdk.deps.aptos.waitForTransaction({
        transactionHash: pendingTransaction.hash,
      });
    },
    onSuccess: () => {
      if (accountAddress) {
        void queryClient.resetQueries({
          queryKey: usdcBalanceQueryKey({ accountAddr: accountAddress }),
        });
        // Restricted Mint only applies to testnet.
        if (isTestnet) {
          void queryClient.resetQueries({
            queryKey: dailyRestrictedMintsRemainingQueryKey(),
          });
          void queryClient.resetQueries({
            queryKey: accountRestrictedMintAvailableQueryKey({
              accountAddr: accountAddress,
            }),
          });
          void queryClient.resetQueries({
            queryKey: triggerResetMintTsQueryKey(),
          });
        }
      }
    },
  });
}
